/**
 * useHeaderTakeover — Manages header visibility and Zone 0 content for page-aware Activity Bar.
 *
 * When the Activity Bar opens on a "takeover" page (patientDetail, patientsList, dashboard),
 * this hook:
 *   1. Adds a CSS class to animate the page header out (collapse + fade, ~200ms)
 *   2. Provides Zone 0 content (title text, back button, action buttons)
 *   3. On close, reverses the animation and restores the normal header
 *
 * Generic contexts and inline mode (ClinicalDayView) skip header takeover.
 *
 * Phase 6: Page-Aware Activity Bar & Header Takeover.
 * See: docs/features/infrastructure/app-shell.md#phase-6-page-aware-activity-bar--header-takeover
 */
import { useEffect, useRef, useCallback } from "react";

/**
 * Contexts that trigger header takeover when the Activity Bar is open.
 */
const TAKEOVER_CONTEXTS = new Set(["dashboard", "dashboardPatientSelected"]);

/**
 * @param {Object}  params
 * @param {string}  params.currentContext - Current detected context key
 * @param {boolean} params.activityBarOpen - Whether the Activity Bar is currently open
 * @param {boolean} params.inline - Whether in inline/dashboard mode (no takeover)
 * @param {Object|null} params.lockedContext - Locked context during active workflows
 * @returns {{
 *   isTakeover: boolean,
 *   takeoverContext: string|null,
 * }}
 */
export function useHeaderTakeover({
    currentContext,
    activityBarOpen,
    inline = false,
    lockedContext = null,
}) {
    const previousContextRef = useRef(null);

    // Determine if takeover is active
    // Use locked context if present (active workflow), otherwise live context
    const effectiveContext = lockedContext?.pageContext || currentContext;
    const isTakeover = !inline && activityBarOpen && TAKEOVER_CONTEXTS.has(effectiveContext);

    // Dashboard inline mode: not a "takeover" (no page header to hide),
    // but we still surface the context so ActivityBarHeader renders the welcome text.
    const DASHBOARD_CONTEXTS = new Set(["dashboard", "dashboardPatientSelected"]);
    const isInlineDashboard = inline && DASHBOARD_CONTEXTS.has(effectiveContext);
    const takeoverContext = isTakeover
        ? effectiveContext
        : isInlineDashboard
            ? effectiveContext
            : null;

    // Toggle CSS class on page header-container elements
    useEffect(() => {
        const headerEl = document.querySelector(".app-shell-content .header-container");
        if (!headerEl) return;

        if (isTakeover) {
            headerEl.classList.add("activity-bar-takeover");
            previousContextRef.current = effectiveContext;
        } else {
            headerEl.classList.remove("activity-bar-takeover");
            previousContextRef.current = null;
        }

        return () => {
            // Cleanup on unmount — ensure header is restored
            headerEl.classList.remove("activity-bar-takeover");
        };
    }, [isTakeover, effectiveContext]);

    return {
        isTakeover,
        takeoverContext,
    };
}
